import { usePathname, useRouter } from 'expo-router'
import { useEffect, useState, type LegacyRef, type ReactNode, type Ref } from 'react'
import { TouchableOpacity, View, type TextInput } from 'react-native'

import { Input } from '@/src/elements/input'
import { Icon } from '@/src/elements/media/Icon'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useDebounce } from '@/src/general/hooks/useDebounce'
import { useDimensions } from '@/src/general/hooks/useDimensions'
import { useSafeSetParams } from '@/src/general/hooks/useSafeSetParams'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

type TSearchBarProps = {
  onChangeText: (value: string) => void
  testID?: string
  autofocus?: true
  ref?: Ref<TextInput>
  searchValue: string
}

export const SearchBar = ({ ref, onChangeText, testID, autofocus, searchValue }: TSearchBarProps): ReactNode => {
  const [value, setValue] = useState(searchValue)
  const debouncedValue = useDebounce(value, 300)
  const pathname = usePathname()
  const { routes } = useRoutes()
  const { safeSetParams } = useSafeSetParams()
  const { navigate } = useRouter()
  const { t } = useTranslate()
  const { xs, sm, xm } = useDimensions()

  const isMobileFormat = xs || sm || xm

  useEffect(() => {
    onChangeText?.(debouncedValue)
    safeSetParams({ search: debouncedValue || undefined })
  }, [debouncedValue, onChangeText, safeSetParams])

  const shouldRedirectToMobile = pathname !== routes.communitiesSearch() && isMobileFormat

  const input = (
    <Input
      autofocus={autofocus}
      testID={testID}
      ref={ref as LegacyRef<TextInput> | undefined}
      startIcon={ICON.SEARCH}
      placeholder={t('navigation.search')}
      endContent={
        value ? (
          <TouchableOpacity testID={TEST_ID.COMMUNITIES.SEARCH_INPUT_CROSS} onPress={() => setValue('')}>
            <Icon pointerEvents="none" name={ICON.CROSS} className="color-default-text-placeholder" />
          </TouchableOpacity>
        ) : null
      }
      className="max-w-screen md:mt-6 md:max-w-[400px]"
      onChangeText={(v) => {
        setValue(v)
      }}
      value={value}
    />
  )

  return shouldRedirectToMobile ? (
    <TouchableOpacity testID={TEST_ID.COMMUNITIES.INPUT_BUTTON} onPress={() => navigate(routes.communitiesSearch())}>
      <View pointerEvents="none">{input}</View>
    </TouchableOpacity>
  ) : (
    input
  )
}
